<template>
  <div
    :class="[
      { 'sub-section': interfaceKey === 'MortgageStartDate' },
      styleClass,
    ]"
  >
    <HomeBaseLabel
      :question="labelConfiguration"
      style="padding-bottom: 0; margin-bottom: 6px"
      :class="[labelStyle]"
    />
    <div class="date-container">
      <div class="date-input day-input">
        <InputMask
          ref="dayInput"
          :question="dayQuestion"
          labelClass="font-16"
          pattern="\d*"
          inputmode="numeric"
          @blur="onBlur"
        ></InputMask>
      </div>
      <div class="date-input month-input">
        <InputMask
          ref="monthInput"
          :question="monthQuestion"
          labelClass="font-16"
          pattern="\d*"
          inputmode="numeric"
          @blur="onBlur"
        />
      </div>
      <div class="date-input year-input">
        <InputMask
          ref="yearInput"
          :question="yearQuestion"
          labelClass="font-16"
          pattern="\d*"
          inputmode="numeric"
          @blur="onBlur"
        />
      </div>
    </div>
    <div class="invisible-input">
      <v-text-field
        :id="questionObject?.Vid"
        v-model="questionObject.Value"
        dense
        outlined
        :rules="getRules"
        ref="dateInput"
        :hide-details="hideDetails"
        :validate-on-blur="true"
      ></v-text-field>
    </div>
  </div>
</template>
<script lang="js">
import QuestionValidation from "@/Mixins/Validation/QuestionValidation";
import { formatTwoDigitNumber } from "@/common/utils";
import HomeBaseLabel from "@/components/HomeComponents/HomeBaseComponents/HomeBaseLabel";
import InputMask from "@/components/Inputs/InputMask";
import moment from "moment";

export default {
  name: "BaseInputDate",
  mixins: [QuestionValidation],
  components: {
    HomeBaseLabel,
    InputMask,
  },
  props: {
    labelClass: {
      type: String,
      default: null
    },
    height: {
      type: Number,
      required: false,
    },
    questionObject: {
      type: Object,
      required: true,
    },
    label: {
      type: String,
      required: true,
    },
    labelStyle: {
      type: String,
      default: null,
    },
    helpText: {
      type: String,
      default: null,
    },
    validation: {
      type: Object,
      required: true
    },
    styleClass: {
      type: String,
      required: false,
    },
    interfaceKey: {
      type: String,
    },
    hideDetails: {
      type:[Boolean, String],
      default: 'auto'
    }
  },
  watch: {
    'dayQuestion.Value'(value) {
      this.assignDateValue();
      this.question.Value = this.questionObject.Value;
      if (!value) {
        this.resetField("dayInput");
      }
      this.focusNext(value, 'month');
    },
    'monthQuestion.Value'(value) {
      this.assignDateValue();
      this.question.Value = this.questionObject.Value;
      if (!value) {
        this.resetField("monthInput");
      }
      this.focusNext(value, 'year');
    },
    'yearQuestion.Value'(value) {
      this.assignDateValue();
      this.question.Value = this.questionObject.Value;
      if (!value) {
        this.resetField("yearInput");
      }
    },
  },
  computed: {
    labelConfiguration() {
      return {
        Text: this.label,
        HelpText: this.helpText,
      }
    },
  },
  data() {
    return {
      question: {
        Value: "",
        Validation: this.validation
      },
      dayQuestion: {
        DataUiid: "DateOfBirth_Day",
        InterfaceKey: "DateOfBirth_Day",
        Text: "Day (DD)",
        AnswerControlAttributePlaceholder: "DD",
        HideDetails: true,
        IsVisible: true,
        Value:  "",
        DisplayType: "Question",
        StyleClass: "remove-input-arrow",
        AnswerControlAttributeType: "text",
        AnswerControlMask: "##",
        Pattern: "\\d*",
        Inputmode: "numeric",
        Validation: {
          Rules: [this.validation.Rules.includes("ruleRequired") ? 'ruleRequired' : '', "ruleValueInRange"],
          RuleProperties: {
            ValueInRange: {
              MaxValue: 31,
              MinValue: 1,
            },
          },
        }
      },
      monthQuestion: {
        DataUiid: "DateOfBirth_Month",
        InterfaceKey: "DateOfBirth_Month",
        Text: "Month (MM)",
        HideDetails: true,
        AnswerControlAttributePlaceholder: "MM",
        IsVisible: true,
        Value:  "",
        AnswerControlMask: "##",
        DisplayType: "Question",
        StyleClass: "remove-input-arrow",
        AnswerControlAttributeType: "number",
        Pattern: "\\d*",
        Inputmode: "numeric",
        Validation: {
          Rules: [this.validation.Rules.includes("ruleRequired") ? 'ruleRequired' : '', "ruleValueInRange"],
          RuleProperties: {
            ValueInRange: {
              MaxValue: 12,
              MinValue: 1,
            }
          },
        }
      },
      yearQuestion: {
        DataUiid: "DateOfBirth_Year",
        InterfaceKey: "DateOfBirth_Year",
        Text: "Year (YYYY)",
        AnswerControlAttributePlaceholder: "YYYY",
        HideDetails: true,
        IsVisible: true,
        Value:  "",
        DisplayType: "Question",
        StyleClass: "remove-input-arrow",
        AnswerControlAttributeType: "text",
        AnswerControlMask: "####",
        Pattern: "\\d*",
        Inputmode: "numeric",
        Validation: {
          Rules: [this.validation.Rules.includes("ruleRequired") ? 'ruleRequired' : '', "ruleMinLength", "ruleValueInRange"],
          RuleProperties: {
            MinLength: {
              Value: 4,
            },
            ValueInRange: {
              MaxValue: 9999,
              MinValue: 1000,
            }
          },
        }
      },
    };
  },
  methods: {
    focusNext(value, nextInputToFocus) {
      if(!value || value.length < 2) {
        return;
      }
      switch(nextInputToFocus) {
        case 'month':
          this.$el.querySelector('[Data-UIID="DateOfBirth_Month"')?.focus();
          return;
        case 'year':
          this.$el.querySelector('[Data-UIID="DateOfBirth_Year"')?.focus();
          return;
        default:
          return;
      }
    },
    resizeHandler() {
      setTimeout(() => this.setLabelBaseOnWindowSize(), 500)
    },
    setLabelBaseOnWindowSize() {
      if(window.screen.availWidth > 850) {
        this.dayQuestion.Text = "Day (DD)";
        this.monthQuestion.Text = "Month (MM)";
        this.yearQuestion.Text = "Year (YYYY)";
      }
      else {
        this.dayQuestion.Text = "Day";
        this.monthQuestion.Text = "Month";
        this.yearQuestion.Text = "Year";
      }
    },
    resetField(field) {
      this.$refs[field].$refs.inputMask.reset();
    },
    onBlur(value) {
      if ((!value && !this.questionObject.Value) || this.questionObject.Value) {
        this.$refs.dateInput.validate(true);
      }
    },
    assignDateValue() {
      const day = this.dayQuestion.Value;
      const month = this.monthQuestion.Value;
      const year = this.yearQuestion.Value;
      let invalidDay = !!day && (day < 1 || day > 31);
      let invalidMonth = !!month && (month < 1 || month > 12);
      let invalidYear = !!year && year < 1000;
      const notHasInvalidDate = !(invalidDay || invalidMonth || invalidYear);
      const missingData = !(day && month && year);

      if (notHasInvalidDate && missingData) {
        this.questionObject.Value = "";
      } else {
        this.questionObject.Value = `${formatTwoDigitNumber(
          day
        )}/${formatTwoDigitNumber(month)}/${invalidYear ? '' : year}`;
      }
    },
    /**
     * check if input string is valid number
     * @param {string} value input string
     */
    isValidNumber(value) {
      return value != null && value != "" && !isNaN(value);
    },
    createInputBaseQuestion(inputId) {
      switch(inputId) {
        case "Day":
          break;
      }
    }
  },
  beforeMount() {
    if(this.questionObject.Value != null && this.questionObject.Value !== '') {
      const date = moment(this.questionObject.Value, "DD/MM/YYYY");
      this.questionObject.value;
      this.dayQuestion.Value = formatTwoDigitNumber(date.date().toString());
      this.monthQuestion.Value = formatTwoDigitNumber((date.month() + 1).toString());
      this.yearQuestion.Value = date.year().toString();
    }
    else {
      this.questionObject.Value = "";
    }
  },
  created() {
    this.setLabelBaseOnWindowSize();
    window.addEventListener("resize", this.resizeHandler)
  },
  destroyed() {
    window.addEventListener("resize", this.resizeHandler)
  },
};
</script>
<style lang="scss" scoped>
.date-container {
  display: flex;
  justify-content: space-between;
}
.date-input {
  :deep(.v-input) {
    .v-input__control {
      .v-input__slot {
        .v-text-field__slot {
          input {
            text-align: center;
          }
        }
      }
    }
  }
}
.invisible-input {
  padding-top: 0;
  padding-bottom: 0;
  :deep(.v-input) {
    padding-top: 0;
    margin-top: 0;
    .v-input__control {
      .v-input__slot {
        display: none;
      }
      .v-text-field__details {
        padding-left: 0;
        padding-right: 0;
        margin-top: 4px;
      }
    }
  }
}

.date-input {
  padding: 0 0;

  :deep(.col-12.col-md-6.pa-0) {
    max-width: 100%;
  }

  &.day-input {
    max-width: calc(30% - 12px);
    :deep(.v-input) {
      margin-bottom: 0 !important;
    }
  }

  &.month-input {
    max-width: calc(30% - 12px);
    :deep(.v-input) {
      margin-bottom: 0 !important;
    }
  }

  &.year-input {
    max-width: calc(40% - 16px);
    :deep(.v-input) {
      margin-bottom: 0 !important;
    }
  }
}
.policy-start-date {
  margin-top: -2px !important;
  margin-bottom: 5px !important;
}
.sub-section {
  margin-bottom: -26px;
}
.mortgage-completion-date {
  margin-top: -3px !important;
}
@media screen and (max-width: 992px) {
  .sub-section {
    margin-bottom: -10px;
  }
}
@media screen and(max-width: ($grid-breakpoints-sm - 1)) {
  .date-input {
    &.day-input {
      max-width: calc(30% - 11px);
    }

    &.month-input {
      max-width: calc(30% - 11px);
    }

    &.year-input {
      max-width: calc(40% - 15px);
    }
  }
}
.remove-input-arrow {
  :deep(.v-text-field__slot) {
    /* Chrome, Safari, Edge, Opera */
    input::-webkit-outer-spin-button,
    input::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    /* Firefox */
    input[type="number"] {
      appearance: textfield;
      -moz-appearance: textfield;
    }
  }
}
</style>
